import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Card,
  CardContent,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  LinearProgress,
  Tooltip,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  DirectionsCar as RidesIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  AccessTime as TimeIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  CardGiftcard as LoyaltyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Refresh as ResetIcon,
  EmojiEvents as TrophyIcon,
  Redeem as RedeemIcon,
  Savings as SavingsIcon,
  LocalHospital as MedicalIcon,
  Science as TesterIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import {
  fetchCustomerById,
  deleteCustomer,
  logoutCustomer,
  addLoyaltyDiscount,
  removeLoyaltyDiscount,
  resetLoyaltyCard,
  addMiles,
  removeMiles,
  setMilesBalance,
  resetMilesCard,
  toggleCustomerTestUser
} from '../../redux/slices/customersSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import { isCentralRole, isOperatorAdminRole } from '../../redux/slices/authSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import CustomerTemplatesSection from './CustomerTemplatesSection';

const InfoCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'visible',
}));

const CustomerAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  marginBottom: 16,
  border: `3px solid ${theme.palette.background.paper}`,
  backgroundColor: theme.palette.primary.main,
}));

const LabelTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
}));

const StatBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.surface[100],
}));

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentCustomer, isLoading, error } = useSelector(state => state.customers);
  const { global: globalSettings } = useSelector(state => state.settings);
  const currentUser = useSelector(state => state.auth.user);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  // Treuemeilen-Verwaltung: ein Dialog mit Modus 'add' | 'remove' | 'set'
  const [milesDialog, setMilesDialog] = useState({ open: false, mode: 'add' });
  const [milesAmount, setMilesAmount] = useState('');
  const [milesSubmitting, setMilesSubmitting] = useState(false);
  const [milesResetOpen, setMilesResetOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    dispatch(fetchCustomerById(id));
    dispatch(fetchSettings());

    return () => {
      // Cleanup
    };
  }, [dispatch, id]);

  const handleEdit = () => {
    navigate(`/kunden/${id}/bearbeiten`);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    dispatch(deleteCustomer(id)).then((result) => {
      if (!result.error) {
        navigate('/kunden');
      }
    });
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    dispatch(logoutCustomer(id)).then((result) => {
      if (!result.error) {
        setSnackbar({ open: true, message: 'Kunde wurde abgemeldet', severity: 'success' });
      }
    });
    setLogoutDialogOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  // Treuekarten-Handler
  const handleAddDiscount = () => {
    dispatch(addLoyaltyDiscount({ customerId: id, amount: 1 })).then((result) => {
      if (!result.error) {
        setSnackbar({ open: true, message: 'Rabatt wurde hinzugefügt', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Fehler beim Hinzufügen', severity: 'error' });
      }
    });
  };

  const handleRemoveDiscount = () => {
    if (currentCustomer?.loyaltyCard?.availableDiscounts > 0) {
      dispatch(removeLoyaltyDiscount({ customerId: id, amount: 1 })).then((result) => {
        if (!result.error) {
          setSnackbar({ open: true, message: 'Rabatt wurde entfernt', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: 'Fehler beim Entfernen', severity: 'error' });
        }
      });
    }
  };

  const handleResetLoyaltyCard = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    dispatch(resetLoyaltyCard(id)).then((result) => {
      if (!result.error) {
        setSnackbar({ open: true, message: 'Treuekarte wurde zurückgesetzt', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Fehler beim Zurücksetzen', severity: 'error' });
      }
    });
    setResetDialogOpen(false);
  };

  // ── Treuemeilen-Verwaltung ──
  const milesDialogConfig = {
    add: { title: 'Meilen gutschreiben', label: 'Anzahl Meilen', confirm: 'Gutschreiben' },
    remove: { title: 'Meilen abziehen', label: 'Anzahl Meilen', confirm: 'Abziehen' },
    set: { title: 'Kontostand setzen', label: 'Neuer Kontostand (Meilen)', confirm: 'Setzen' }
  };

  const handleMilesDialogOpen = (mode) => {
    setMilesAmount(mode === 'set' ? String(currentCustomer.milesCard?.balance ?? 0) : '');
    setMilesDialog({ open: true, mode });
  };

  const handleMilesDialogClose = () => {
    if (milesSubmitting) return;
    setMilesDialog(prev => ({ ...prev, open: false }));
  };

  const handleMilesDialogSubmit = async () => {
    const value = parseInt(milesAmount, 10);
    if (Number.isNaN(value) || value < 0 || (milesDialog.mode !== 'set' && value === 0)) {
      setSnackbar({ open: true, message: 'Bitte eine gültige Meilenzahl eingeben', severity: 'error' });
      return;
    }
    setMilesSubmitting(true);
    try {
      if (milesDialog.mode === 'add') {
        await dispatch(addMiles({ customerId: id, amount: value })).unwrap();
        setSnackbar({ open: true, message: `${value} Meilen gutgeschrieben`, severity: 'success' });
      } else if (milesDialog.mode === 'remove') {
        await dispatch(removeMiles({ customerId: id, amount: value })).unwrap();
        setSnackbar({ open: true, message: `${value} Meilen abgezogen`, severity: 'success' });
      } else {
        await dispatch(setMilesBalance({ customerId: id, newBalance: value })).unwrap();
        setSnackbar({ open: true, message: `Kontostand auf ${value} Meilen gesetzt`, severity: 'success' });
      }
      setMilesDialog(prev => ({ ...prev, open: false }));
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    } finally {
      setMilesSubmitting(false);
    }
  };

  const handleMilesResetConfirm = () => {
    dispatch(resetMilesCard(id)).then((result) => {
      setSnackbar(result.error
        ? { open: true, message: 'Fehler beim Zurücksetzen', severity: 'error' }
        : { open: true, message: 'Treuemeilen-Konto wurde zurückgesetzt', severity: 'success' });
    });
    setMilesResetOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Nie';
    const date = timestamp.toDate
      ? timestamp.toDate()
      : timestamp.seconds
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatBirthDate = (value) => {
    if (!value) return 'Nicht gesetzt';
    const date = value.toDate
      ? value.toDate()
      : value.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);
    if (Number.isNaN(date.getTime())) return 'Nicht gesetzt';
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const formatEligibility = (eligibility = {}) => {
    const marks = Array.isArray(eligibility.mobilityMarks) ? eligibility.mobilityMarks : [];
    const careLevel = eligibility.careLevel ? String(eligibility.careLevel) : '';
    const hasPermanentImpairment = eligibility.permanentMobilityImpairment === true;
    const eligibleByMarks = marks.some(mark => ['aG', 'Bl', 'H'].includes(mark));
    const eligibleByCareLevel = ['4', '5'].includes(careLevel) || (careLevel === '3' && hasPermanentImpairment);
    const needsReview = careLevel === '3' && !hasPermanentImpairment;

    return {
      marksText: marks.length ? marks.join(', ') : 'Nicht hinterlegt',
      careLevelText: careLevel ? `Pflegegrad ${careLevel}` : 'Nicht hinterlegt',
      impairmentText: hasPermanentImpairment ? 'Ja' : 'Nein',
      status: eligibleByMarks || eligibleByCareLevel
        ? 'Personenkreis nach §8 Abs. 3 wahrscheinlich erfüllt'
        : needsReview
          ? 'Pflegegrad 3: dauerhafte Mobilitätsbeeinträchtigung manuell prüfen'
          : 'Nicht hinterlegt / manuell prüfen',
      severity: eligibleByMarks || eligibleByCareLevel ? 'success' : 'warning'
    };
  };

  if (isLoading) {
    return <Loading message="Kundendaten werden geladen..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Kundendaten"
        message={error}
        onRetry={() => dispatch(fetchCustomerById(id))}
      />
    );
  }

  if (!currentCustomer) {
    return (
      <ErrorMessage
        title="Kunde nicht gefunden"
        message="Der angeforderte Kunde konnte nicht gefunden werden."
        onRetry={() => navigate('/kunden')}
      />
    );
  }

  // Treuekarten-Daten
  const loyaltyCard = currentCustomer.loyaltyCard || {
    currentRideCount: 0,
    availableDiscounts: 0,
    totalEarnedDiscounts: 0,
    totalRedeemedDiscounts: 0,
    totalSavingsEur: 0
  };
  const ridesRequired = globalSettings?.loyaltyCardRidesRequired || 5;
  const discountAmount = globalSettings?.loyaltyCardDiscountEur || 5;
  const loyaltyActive = globalSettings?.loyaltyCardActive || false;
  const progressPercent = (loyaltyCard.currentRideCount / ridesRequired) * 100;
  const customerManagingOperatorId = currentCustomer.user?.managedByOperatorId ||
    currentCustomer.user?.createdByOperatorId ||
    currentCustomer.user?.operatorId;
  const canManageCustomer = isCentralRole(currentUser) || (
    isOperatorAdminRole(currentUser) &&
    currentCustomer.user?.isPhoneCustomer === true &&
    customerManagingOperatorId === currentUser?.operatorId
  );
  const canManageLoyalty = isCentralRole(currentUser);
  // Tester-Freigabe des Testmodus: hebt fuer dieses Konto die Bestellsperre der
  // Kunden-App auf. Nur die Zentrale darf sie setzen — die Firestore-Rules
  // verweigern das Feld dem Kunden selbst und den Unternehmer-Rollen.
  // nextcab_mobile/docs/general/testmodus_kunden_app_konzept.md
  const canManageTestUser = isCentralRole(currentUser);
  const isTestUser = currentCustomer.isTestUser === true;
  const testModeActive = globalSettings?.testModusAktiv === true;

  const handleToggleTestUser = () => {
    dispatch(toggleCustomerTestUser({ id: currentCustomer.id, isTestUser: !isTestUser }));
  };
  // Das Treueprogramm gilt ausschließlich für App-Kunden. Telefonkunden werden
  // serverseitig ohnehin nicht gutgeschrieben (onRideCompletedLoyalty überspringt
  // isPhoneCustomer), daher wird die Treuekarte für sie gar nicht erst angezeigt.
  const isPhoneCustomer = currentCustomer.user?.isPhoneCustomer === true;

  // Treueprogramm-Modus (exklusiv): 'off' | 'stamp' | 'miles'
  const programMode = globalSettings?.loyaltyProgramMode
    || (globalSettings?.loyaltyCardActive ? 'stamp' : 'off');

  // Treuemeilen-Konto (System 2)
  const milesCard = currentCustomer.milesCard || {
    balance: 0,
    totalEarned: 0,
    totalRedeemedBlocks: 0,
    totalSavingsEur: 0
  };
  const milesPerBlock = globalSettings?.milesPerBlock || 0;
  const milesBlockEur = globalSettings?.milesBlockEur || 0;
  const availableMilesBlocks = milesPerBlock > 0
    ? Math.floor((milesCard.balance || 0) / milesPerBlock)
    : 0;
  const milesProgressPercent = milesPerBlock > 0
    ? ((milesCard.balance % milesPerBlock) / milesPerBlock) * 100
    : 0;

  // Karten nur für App-Kunden; je Karte nur, wenn das Programm aktiv ist ODER
  // noch ein Bestand vorhanden ist (nach einem Modus-Wechsel eingefroren).
  const hasStampActivity = (loyaltyCard.currentRideCount || 0) > 0
    || (loyaltyCard.availableDiscounts || 0) > 0
    || (loyaltyCard.totalEarnedDiscounts || 0) > 0;
  const hasMilesActivity = (milesCard.balance || 0) > 0 || (milesCard.totalEarned || 0) > 0;
  const showStampCard = !isPhoneCustomer && (programMode === 'stamp' || hasStampActivity);
  const showMilesCard = !isPhoneCustomer && (programMode === 'miles' || hasMilesActivity);

  const eligibility = currentCustomer.user?.medicalTransportEligibility || {};
  const eligibilityInfo = formatEligibility(eligibility);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Kundendetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Kunden', path: '/kunden' },
          { label: currentCustomer.user?.name || 'Details', path: `/kunden/${id}` }
        ]}
        actionButton={canManageCustomer ? {
          label: 'Bearbeiten',
          icon: <EditIcon />,
          onClick: handleEdit
        } : null}
      />

      <Grid container spacing={3}>
        {/* Linke Spalte - Kundeninfos */}
        <Grid item xs={12} md={4}>
          <InfoCard elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CustomerAvatar src={currentCustomer.user?.profile_picture || ''}>
                {currentCustomer.user?.name ? currentCustomer.user.name.charAt(0) : 'K'}
              </CustomerAvatar>
              <Typography variant="h5" gutterBottom>
                {currentCustomer.user?.name}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Chip
                  label={currentCustomer.isOnline ? 'Online' : 'Offline'}
                  color={currentCustomer.isOnline ? 'success' : 'default'}
                  icon={currentCustomer.isOnline ? <OnlineIcon /> : <OfflineIcon />}
                />
              </Box>
              <Divider sx={{ my: 2 }} />

              <List sx={{ width: '100%' }}>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>E-Mail</LabelTypography>}
                    secondary={currentCustomer.user?.email || 'Keine E-Mail'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Telefon</LabelTypography>}
                    secondary={currentCustomer.user?.phoneNumber || 'Keine Telefonnummer'}
                  />
                </ListItem>
                {/* Quittungs-E-Mail nur bei Telefonkunden (Quittungsversand 4.5):
                    vom Fahrer beim Abschluss erfasst oder hier gepflegt */}
                {isPhoneCustomer && (
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={<LabelTypography>E-Mail für Quittungen</LabelTypography>}
                      secondary={currentCustomer.user?.receiptEmail ||
                        'Nicht hinterlegt — der Fahrer fragt beim Fahrtabschluss (bei aktivem Quittungsversand)'}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Geburtsdatum</LabelTypography>}
                    secondary={formatBirthDate(currentCustomer.user?.birthDate)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <RidesIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Fahrten</LabelTypography>}
                    secondary={`${currentCustomer.completed_rides || 0} abgeschlossen / ${currentCustomer.total_rides || 0} gesamt`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TimeIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Letzter Login</LabelTypography>}
                    secondary={formatDate(currentCustomer.lastLogin)}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isCentralRole(currentUser) && currentCustomer.isOnline && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    fullWidth
                  >
                    Kunde abmelden
                  </Button>
                )}
                {canManageCustomer && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    fullWidth
                  >
                    Kunde deaktivieren
                  </Button>
                )}
              </Box>
            </CardContent>
          </InfoCard>

          <InfoCard elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Krankenfahrten-Personenkreis
              </Typography>
              <List sx={{ width: '100%' }}>
                <ListItem>
                  <ListItemIcon>
                    <MedicalIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Merkzeichen</LabelTypography>}
                    secondary={eligibilityInfo.marksText}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Pflegegrad</LabelTypography>}
                    secondary={eligibilityInfo.careLevelText}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <RidesIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Dauerhafte Mobilitätsbeeinträchtigung</LabelTypography>}
                    secondary={eligibilityInfo.impairmentText}
                  />
                </ListItem>
              </List>
              <Alert severity={eligibilityInfo.severity} sx={{ mt: 1 }}>
                {eligibilityInfo.status}
              </Alert>
              {eligibility.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                  {eligibility.notes}
                </Typography>
              )}
            </CardContent>
          </InfoCard>

          {/* Testmodus der Kunden-App: Tester-Freigabe fuer dieses Konto.
              nextcab_mobile/docs/general/testmodus_kunden_app_konzept.md */}
          {canManageTestUser && !isPhoneCustomer && (
            <InfoCard elevation={3}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TesterIcon color={isTestUser ? 'info' : 'disabled'} />
                  <Typography variant="h6">Testmodus</Typography>
                  {isTestUser && (
                    <Chip label="Tester" size="small" color="info" variant="outlined" />
                  )}
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isTestUser}
                      onChange={handleToggleTestUser}
                      color="info"
                    />
                  }
                  label="Darf trotz Testmodus bestellen"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {isTestUser
                    ? 'Dieses Konto kann auch bei aktivem Testmodus in der App bestellen. Die Bestellungen sind echte Fahrten und gehen in die Disposition.'
                    : 'Ohne Freigabe sieht dieses Konto bei aktivem Testmodus nur den Hinweis und kann nicht bestellen.'}
                </Typography>
                {isTestUser && !testModeActive && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Der Testmodus ist derzeit nicht aktiv — die Freigabe hat gerade
                    keine Wirkung, bleibt aber erhalten.
                  </Alert>
                )}
              </CardContent>
            </InfoCard>
          )}
        </Grid>

        {/* Rechte Spalte */}
        <Grid item xs={12} md={8}>
          {/* Treuekarten-Karte – App-Kunden, wenn Stempel-System aktiv oder Bestand vorhanden */}
          {showStampCard && (
          <InfoCard elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LoyaltyIcon color="primary" />
                  <Typography variant="h6">
                    Treuekarten-Status
                  </Typography>
                </Box>
                <Chip
                  label={loyaltyActive ? 'System aktiv' : 'System inaktiv'}
                  color={loyaltyActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Divider sx={{ mb: 3 }} />

              {!loyaltyActive && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Das Treuekarten-System ist derzeit deaktiviert. Die Daten werden trotzdem angezeigt und können verwaltet werden.
                </Alert>
              )}

              {/* Fortschrittsanzeige */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Fortschritt zum nächsten Rabatt
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {loyaltyCard.currentRideCount} von {ridesRequired} Fahrten
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'surface.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: progressPercent >= 100 ? 'success.main' : 'primary.main',
                      borderRadius: 5
                    }
                  }}
                />
                {progressPercent >= 80 && progressPercent < 100 && (
                  <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                    Nur noch {ridesRequired - loyaltyCard.currentRideCount} Fahrt(en) bis zum nächsten Rabatt!
                  </Typography>
                )}
              </Box>

              {/* Statistiken */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <RedeemIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {loyaltyCard.availableDiscounts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verfügbar
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({loyaltyCard.availableDiscounts * discountAmount}€)
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <TrophyIcon sx={{ fontSize: 32, mb: 1, color: 'gold' }} />
                    <Typography variant="h4" fontWeight="bold">
                      {loyaltyCard.totalEarnedDiscounts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verdient
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <LoyaltyIcon sx={{ fontSize: 32, mb: 1, color: 'green' }} />
                    <Typography variant="h4" fontWeight="bold">
                      {loyaltyCard.totalRedeemedDiscounts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Eingelöst
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <SavingsIcon sx={{ fontSize: 32, mb: 1, color: 'green' }} />
                    <Typography variant="h4" fontWeight="bold">
                      {loyaltyCard.totalSavingsEur}€
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gespart
                    </Typography>
                  </StatBox>
                </Grid>
              </Grid>

              {/* Verwaltungs-Buttons */}
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Verwaltung
              </Typography>
              {canManageLoyalty && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Tooltip title="Rabatt manuell gutschreiben (z.B. als Entschuldigung)">
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={handleAddDiscount}
                  >
                    Rabatt hinzufügen
                  </Button>
                </Tooltip>
                <Tooltip title="Verfügbaren Rabatt entfernen">
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<RemoveIcon />}
                      onClick={handleRemoveDiscount}
                      disabled={loyaltyCard.availableDiscounts === 0}
                    >
                      Rabatt entfernen
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Alle Treuekarten-Daten zurücksetzen">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ResetIcon />}
                    onClick={handleResetLoyaltyCard}
                  >
                    Zurücksetzen
                  </Button>
                </Tooltip>
              </Box>
              )}

              {loyaltyCard.lastUpdated && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Letzte Aktualisierung: {formatDate(loyaltyCard.lastUpdated)}
                </Typography>
              )}
            </CardContent>
          </InfoCard>
          )}

          {/* Treuemeilen-Konto – App-Kunden, wenn Meilen-System aktiv oder Bestand vorhanden */}
          {showMilesCard && (
          <InfoCard elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SavingsIcon color="primary" />
                  <Typography variant="h6">Treuemeilen-Konto</Typography>
                </Box>
                <Chip
                  label={programMode === 'miles' ? 'System aktiv' : 'System inaktiv'}
                  color={programMode === 'miles' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Divider sx={{ mb: 3 }} />

              {programMode !== 'miles' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Das Treuemeilen-System ist derzeit nicht aktiv. Der Bestand bleibt erhalten und
                  ist wieder einlösbar, sobald das System aktiviert wird.
                </Alert>
              )}
              {programMode === 'miles' && (milesPerBlock <= 0 || milesBlockEur <= 0) && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Es sind noch keine Einlöse-Werte gesetzt (Meilen je Block / Rabatt je Block).
                  Bitte in den Einstellungen konfigurieren.
                </Alert>
              )}

              {/* Guthaben + Fortschritt zum nächsten Block */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Meilenguthaben</Typography>
                  <Typography variant="body2" fontWeight="bold">{milesCard.balance} Meilen</Typography>
                </Box>
                {milesPerBlock > 0 && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={milesProgressPercent}
                      sx={{
                        height: 10, borderRadius: 5, bgcolor: 'surface.200',
                        '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 5 }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Noch {milesPerBlock - (milesCard.balance % milesPerBlock)} Meilen bis zum nächsten Block ({milesBlockEur}€).
                    </Typography>
                  </>
                )}
              </Box>

              {/* Statistiken */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <RedeemIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="primary">{availableMilesBlocks}</Typography>
                    <Typography variant="body2" color="text.secondary">Blöcke verfügbar</Typography>
                    <Typography variant="caption" color="text.secondary">({availableMilesBlocks * milesBlockEur}€)</Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <TrophyIcon sx={{ fontSize: 32, mb: 1, color: 'gold' }} />
                    <Typography variant="h4" fontWeight="bold">{milesCard.totalEarned}</Typography>
                    <Typography variant="body2" color="text.secondary">Meilen gesammelt</Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <LoyaltyIcon sx={{ fontSize: 32, mb: 1, color: 'green' }} />
                    <Typography variant="h4" fontWeight="bold">{milesCard.totalRedeemedBlocks}</Typography>
                    <Typography variant="body2" color="text.secondary">Blöcke eingelöst</Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <SavingsIcon sx={{ fontSize: 32, mb: 1, color: 'green' }} />
                    <Typography variant="h4" fontWeight="bold">{milesCard.totalSavingsEur}€</Typography>
                    <Typography variant="body2" color="text.secondary">Gespart</Typography>
                  </StatBox>
                </Grid>
              </Grid>

              {/* Verwaltung */}
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Verwaltung</Typography>
              {canManageLoyalty && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Tooltip title="Meilen manuell gutschreiben">
                    <Button variant="outlined" color="success" startIcon={<AddIcon />} onClick={() => handleMilesDialogOpen('add')}>
                      Meilen gutschreiben
                    </Button>
                  </Tooltip>
                  <Tooltip title="Meilen manuell abziehen">
                    <span>
                      <Button variant="outlined" color="warning" startIcon={<RemoveIcon />} onClick={() => handleMilesDialogOpen('remove')} disabled={(milesCard.balance || 0) === 0}>
                        Meilen abziehen
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Kontostand direkt setzen">
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => handleMilesDialogOpen('set')}>
                      Kontostand setzen
                    </Button>
                  </Tooltip>
                  <Tooltip title="Treuemeilen-Konto zurücksetzen">
                    <Button variant="outlined" color="error" startIcon={<ResetIcon />} onClick={() => setMilesResetOpen(true)}>
                      Zurücksetzen
                    </Button>
                  </Tooltip>
                </Box>
              )}

              {milesCard.lastUpdated && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Letzte Aktualisierung: {formatDate(milesCard.lastUpdated)}
                </Typography>
              )}
            </CardContent>
          </InfoCard>
          )}

          {/* Fahrtenhistorie */}
          <InfoCard elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fahrtenhistorie
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {currentCustomer.total_rides > 0 ? (
                <Typography variant="body1">
                  Anzahl der Fahrten: {currentCustomer.total_rides} (davon {currentCustomer.completed_rides || 0} abgeschlossen)
                </Typography>
              ) : (
                <Typography variant="body1">
                  Dieser Kunde hat noch keine Fahrten gebucht.
                </Typography>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to={`/fahrten?customerId=${id}`}
                >
                  Alle Fahrten anzeigen
                </Button>
              </Box>
            </CardContent>
          </InfoCard>

          {/* Vorlagen des Kunden */}
          <CustomerTemplatesSection
            customerId={id}
            customerName={currentCustomer.user?.name}
            customerPhone={currentCustomer.user?.phoneNumber}
          />

          {/* Kundeninformationen */}
          <InfoCard elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kundeninformationen
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Kunde-ID</LabelTypography>
                  <Typography variant="body1" gutterBottom>{id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Name</LabelTypography>
                  <Typography variant="body1" gutterBottom>{currentCustomer.user?.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">E-Mail</LabelTypography>
                  <Typography variant="body1" gutterBottom>{currentCustomer.user?.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Telefon</LabelTypography>
                  <Typography variant="body1" gutterBottom>
                    {currentCustomer.user?.phoneNumber || 'Keine Telefonnummer'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Geburtsdatum</LabelTypography>
                  <Typography variant="body1" gutterBottom>
                    {formatBirthDate(currentCustomer.user?.birthDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Online Status</LabelTypography>
                  <Typography variant="body1" gutterBottom>
                    <Chip
                      size="small"
                      label={currentCustomer.isOnline ? 'Online' : 'Offline'}
                      color={currentCustomer.isOnline ? 'success' : 'default'}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Zuletzt Online</LabelTypography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(currentCustomer.lastLogin)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>

      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Kunde löschen"
        content={`Möchten Sie den Kunden ${currentCustomer.user?.name} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Logout-Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
      >
        <DialogTitle>Kunde abmelden</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Kunden {currentCustomer.user?.name} wirklich abmelden?
            Der Kunde wird in der App ausgeloggt und muss sich neu anmelden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Abbrechen</Button>
          <Button onClick={handleLogoutConfirm} color="warning" variant="contained">
            Abmelden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset-Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
      >
        <DialogTitle>Treuekarte zurücksetzen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Treuekarte von {currentCustomer.user?.name} wirklich zurücksetzen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Alle Treuekarten-Daten (Fortschritt, verfügbare Rabatte, Statistiken) werden gelöscht.
            Dieser Vorgang kann nicht rückgängig gemacht werden.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleResetConfirm} color="error" variant="contained">
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Treuemeilen: Betrag-Dialog (gutschreiben / abziehen / setzen) */}
      <Dialog open={milesDialog.open} onClose={handleMilesDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{milesDialogConfig[milesDialog.mode].title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Kunde: {currentCustomer.user?.name}. Aktueller Kontostand: {currentCustomer.milesCard?.balance ?? 0} Meilen.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label={milesDialogConfig[milesDialog.mode].label}
            value={milesAmount}
            onChange={(e) => setMilesAmount(e.target.value)}
            disabled={milesSubmitting}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMilesDialogClose} disabled={milesSubmitting}>Abbrechen</Button>
          <Button onClick={handleMilesDialogSubmit} variant="contained" color="primary" disabled={milesSubmitting}>
            {milesSubmitting ? 'Wird gespeichert…' : milesDialogConfig[milesDialog.mode].confirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Treuemeilen: Reset-Dialog */}
      <Dialog open={milesResetOpen} onClose={() => setMilesResetOpen(false)}>
        <DialogTitle>Treuemeilen-Konto zurücksetzen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie das Treuemeilen-Konto von {currentCustomer.user?.name} wirklich zurücksetzen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Guthaben und Statistiken werden gelöscht. Dieser Vorgang kann nicht rückgängig gemacht werden.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMilesResetOpen(false)}>Abbrechen</Button>
          <Button onClick={handleMilesResetConfirm} color="error" variant="contained">
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerDetails;
